import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2, Loader2, ShieldAlert, UserRoundX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { InviteLinkShell } from "@/components/teams/InviteLinkShell";
import { ApiError } from "@/lib/api";
import type { InvitationTokenLookup } from "@/lib/api/teams";
import {
  useDeclineInvitationByToken,
  useInvitationTokenLookup,
} from "@/lib/api/teams.queries";

/**
 * Landing page for the WhatsApp "Cancelar" URL button (PF-345). Fully public —
 * declining an invitation must never force account creation, so the cancel
 * token alone is the capability. Renders a confirm step (no GET side effects)
 * and is idempotent on re-taps.
 */
export const Route = createFileRoute("/invite/decline/$token")({
  component: InviteDeclinePage,
});

function InviteDeclinePage() {
  const { token } = Route.useParams();
  return (
    <InviteLinkShell>
      <InviteDeclineContent token={token} />
    </InviteLinkShell>
  );
}

function InviteDeclineContent({ token }: { token: string }) {
  const { t } = useTranslation();
  const lookup = useInvitationTokenLookup(token);

  if (lookup.isPending) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        {t("invite_link.loading")}
      </div>
    );
  }

  if (lookup.isError || !lookup.data || lookup.data.kind !== "decline") {
    return (
      <StateCard
        title={t("invite_link.not_found.title")}
        body={t("invite_link.not_found.body")}
      />
    );
  }

  const invitation = lookup.data;
  switch (invitation.status) {
    case "declined":
      return (
        <SuccessCard
          title={t("invite_link.decline.success.title")}
          body={t("invite_link.decline.success.body")}
        />
      );
    case "cancelled":
      return (
        <StateCard
          title={t("invite_link.cancelled.title")}
          body={t("invite_link.cancelled.body")}
        />
      );
    case "accepted":
      return (
        <StateCard
          title={t("invite_link.accepted.title")}
          body={t("invite_link.decline.already_accepted_body")}
        />
      );
    case "expired":
      return (
        <StateCard
          title={t("invite_link.expired.title")}
          body={t("invite_link.decline.expired_body")}
        />
      );
    default:
      return <PendingDecline token={token} invitation={invitation} />;
  }
}

function PendingDecline({
  token,
  invitation,
}: {
  token: string;
  invitation: InvitationTokenLookup;
}) {
  const { t } = useTranslation();
  const decline = useDeclineInvitationByToken();
  const [declined, setDeclined] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const summary = invitation.inviterName
    ? t("invite_link.summary", {
        inviter: invitation.inviterName,
        team: invitation.teamName,
        role: t(`teams.role.${invitation.role}`, invitation.role),
      })
    : t("invite_link.summary_no_inviter", {
        team: invitation.teamName,
        role: t(`teams.role.${invitation.role}`, invitation.role),
      });

  if (declined) {
    return (
      <SuccessCard
        title={t("invite_link.decline.success.title")}
        body={t("invite_link.decline.success.body")}
      />
    );
  }

  const onDecline = () => {
    setErrorMessage(null);
    decline.mutate(token, {
      onSuccess: () => setDeclined(true),
      onError: (err) =>
        setErrorMessage(
          err instanceof ApiError
            ? err.message
            : t("invite_link.decline.error.generic"),
        ),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <UserRoundX className="h-5 w-5 text-destructive" />
          {t("invite_link.decline.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="text-sm">{summary}</p>
        <p className="text-sm text-muted-foreground">
          {t("invite_link.decline.confirm_hint")}
        </p>

        {errorMessage ? (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <ShieldAlert className="mt-0.5 h-4 w-4 flex-none" />
            <span>{errorMessage}</span>
          </div>
        ) : null}

        <Button
          size="lg"
          variant="destructive"
          onClick={onDecline}
          disabled={decline.isPending}
        >
          {decline.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <UserRoundX className="h-4 w-4" />
          )}
          {t("invite_link.decline.cta")}
        </Button>
        <p className="text-center text-xs text-muted-foreground">
          {t("invite_link.decline.keep_hint")}
        </p>
      </CardContent>
    </Card>
  );
}

function SuccessCard({ title, body }: { title: string; body: string }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
        <CheckCircle2 className="h-10 w-10 text-primary" />
        <p className="text-lg font-semibold">{title}</p>
        <p className="text-sm text-muted-foreground">{body}</p>
      </CardContent>
    </Card>
  );
}

function StateCard({ title, body }: { title: string; body: string }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
        <ShieldAlert className="h-10 w-10 text-muted-foreground" />
        <p className="text-lg font-semibold">{title}</p>
        <p className="text-sm text-muted-foreground">{body}</p>
        <p className="text-xs text-muted-foreground">{t("invite_link.footer_hint")}</p>
      </CardContent>
    </Card>
  );
}
